import { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useCatalog, BlogModel } from "../context/CatalogContext";
import { useRequest } from "../context/RequestContext";

const TEAL = "#4abcc9";
const NAVY = "#0d1f2d";

const CATEGORIES = ["Todos", "Medicamentos", "Salud Mental", "Nutrición", "Prevención"];

const TAG_COLORS: Record<string, string> = {
    "Medicamentos": "#e0f2fe",
    "Salud Mental": "#ede9fe",
    "Nutrición": "#d1fae5",
    "Prevención": "#fef3c7",
};
const TAG_TEXT_COLORS: Record<string, string> = {
    "Medicamentos": "#0369a1",
    "Salud Mental": "#6d28d9",
    "Nutrición": "#065f46",
    "Prevención": "#92400e",
};

const getCategory = (titulo: string): string => {
    const t = titulo.toLowerCase();
    if (/medic|pastilla|fármaco|farmaco|dosis|tratamiento|antibiótico|antibi|prescripci|receta/.test(t)) return "Medicamentos";
    if (/mental|ansiedad|depresión|depresion|estrés|estres|psicol|emocional|ánimo|animo|insomnio/.test(t)) return "Salud Mental";
    if (/nutrici|alimento|dieta|vitamina|mineral|proteína|proteina|carbohidrato|obesidad|peso/.test(t)) return "Nutrición";
    if (/prevención|prevencio|vacuna|hábito|habito|ejercicio|chequeo|screening|citolog/.test(t)) return "Prevención";
    return "Prevención";
};

/* ─── Blog Detail Modal ─────────────────────────────────────────── */
function BlogModal({ blogId, onClose }: { blogId: string; onClose: () => void }) {
    const [detail, setDetail] = useState<BlogModel | null>(null);
    const [loading, setLoading] = useState(true);
    const { sendRequest } = useRequest();

    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await sendRequest<BlogModel>({ path: `/api/blog/${blogId}`, hideLoader: true });
            if (!res.hasError) setDetail(res.data);
            setLoading(false);
        })();
    }, [blogId]);

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handler);
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
    }, [onClose]);

    const tag = detail ? getCategory(detail.titulo) : "";

    return (
        /* Backdrop */
        <div
            onClick={onClose}
            style={{ position: "fixed", inset: 0, background: "rgba(13,31,45,0.75)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}
        >
            {/* Panel */}
            <div
                onClick={e => e.stopPropagation()}
                style={{ background: "white", borderRadius: 24, width: "100%", maxWidth: 780, position: "relative", animation: "slideUp 0.28s cubic-bezier(.16,1,.3,1)", boxShadow: "0 32px 80px rgba(0,0,0,0.25)" }}
            >
                <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(32px); } to { opacity:1; transform:translateY(0); } }`}</style>

                {/* Top bar */}
                <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a4a 100%)`, borderRadius: "24px 24px 0 0", padding: "32px 36px 28px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", inset: 0, opacity: 0.06, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
                    <button
                        onClick={onClose}
                        style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 1 }}
                    >
                        <span className="material-symbols-outlined" style={{ color: "white", fontSize: 20 }}>close</span>
                    </button>
                    {detail && (
                        <div style={{ position: "relative", zIndex: 1 }}>
                            <span style={{ display: "inline-block", background: TAG_COLORS[tag] || TEAL, color: TAG_TEXT_COLORS[tag] || "white", fontFamily: "'Montserrat',sans-serif", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, padding: "4px 14px", borderRadius: 50, marginBottom: 16 }}>
                                {tag}
                            </span>
                            <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "clamp(1.3rem,3vw,1.9rem)", fontWeight: 800, color: "white", margin: "0 0 20px", lineHeight: 1.25 }}>
                                {detail.titulo}
                            </h2>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{ width: 36, height: 36, borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: "white" }}>person</span>
                                </div>
                                <div>
                                    <div style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600, fontSize: "0.875rem" }}>{detail.autor || "HeyDoctor"}</div>
                                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.78rem" }}>
                                        {format(new Date(detail.fechaPublicacion), "dd 'de' MMMM, yyyy", { locale: es })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {loading && (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ width: 20, height: 20, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: TEAL, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" }}>Cargando artículo…</span>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div style={{ padding: "36px 36px 40px" }}>
                    {loading ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {[1, 0.8, 0.9, 0.7, 0.85].map((w, i) => (
                                <div key={i} style={{ height: 16, background: "#f0f4f8", borderRadius: 8, width: `${w * 100}%` }} />
                            ))}
                        </div>
                    ) : detail?.contenido ? (
                        <div
                            dangerouslySetInnerHTML={{ __html: detail.contenido.replaceAll("\n", "<br/>") }}
                            style={{ fontFamily: "'Manrope',sans-serif", fontSize: "0.97rem", lineHeight: 1.8, color: "#374151" }}
                        />
                    ) : (
                        <p style={{ color: "#6b7280", textAlign: "center", padding: "24px 0" }}>No hay contenido disponible.</p>
                    )}
                </div>

                {/* Footer */}
                <div style={{ borderTop: "1px solid #f0f4f8", padding: "20px 36px", display: "flex", justifyContent: "flex-end" }}>
                    <button
                        onClick={onClose}
                        style={{ background: "transparent", border: "1.5px solid #e5e7eb", color: "#6b7280", fontFamily: "'Montserrat',sans-serif", fontWeight: 600, fontSize: "0.875rem", padding: "10px 24px", borderRadius: 10, cursor: "pointer" }}
                    >
                        Cerrar artículo
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Blog Page ─────────────────────────────────────────────────── */
export default function BlogPage() {
    const { blogCatalog } = useCatalog();
    const [activeCategory, setActiveCategory] = useState("Todos");
    const [search, setSearch] = useState("");
    const [openBlogId, setOpenBlogId] = useState<string | null>(null);

    useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

    const handleOpen = useCallback((id: string) => setOpenBlogId(id), []);
    const handleClose = useCallback(() => setOpenBlogId(null), []);

    const filtered = blogCatalog.filter(b => {
        const matchesSearch = b.titulo.toLowerCase().includes(search.toLowerCase()) ||
            (b.autor && b.autor.toLowerCase().includes(search.toLowerCase()));
        const matchesCategory = activeCategory === "Todos" || getCategory(b.titulo) === activeCategory;
        return matchesSearch && matchesCategory;
    });

    const featured = filtered[0];
    const rest = filtered.slice(1);

    return (
        <div style={{ fontFamily: "'Manrope', sans-serif", overflowX: "hidden" }}>

            {/* Modal */}
            {openBlogId && <BlogModal blogId={openBlogId} onClose={handleClose} />}

            {/* ═══ HERO ═══ */}
            <section style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a4a 60%, #0e4a5a 100%)`, padding: "56px 24px 48px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, opacity: 0.07, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                <div style={{ position: "absolute", right: "-80px", top: "-80px", width: 400, height: 400, background: "radial-gradient(circle, rgba(74,188,201,0.22) 0%, transparent 65%)", pointerEvents: "none" }} />
                <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                        <div style={{ width: 36, height: 3, background: TEAL, borderRadius: 2 }} />
                        <span style={{ color: TEAL, fontFamily: "'Montserrat',sans-serif", fontSize: "0.78rem", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const }}>
                            Salud & Bienestar
                        </span>
                    </div>
                    <h1 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "clamp(2.2rem, 5vw, 3.6rem)", fontWeight: 900, color: "#ffffff", lineHeight: 1.1, margin: "0 0 16px", letterSpacing: "-1px" }}>
                        Blog de <span style={{ color: TEAL }}>HeyDoctor</span>
                    </h1>
                    <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "1.05rem", lineHeight: 1.6, maxWidth: 560, margin: "0 0 32px" }}>
                        Artículos médicos escritos por especialistas para ayudarte a entender mejor tu salud y bienestar.
                    </p>
                    <div style={{ display: "flex", maxWidth: 500, gap: 0 }}>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,0.2)", borderRadius: "12px 0 0 12px", padding: "0 16px", gap: 10 }}>
                            <span className="material-symbols-outlined" style={{ color: "rgba(255,255,255,0.5)", fontSize: 20 }}>search</span>
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar artículos..."
                                style={{ background: "none", border: "none", outline: "none", color: "white", fontFamily: "'Manrope',sans-serif", fontSize: "0.95rem", width: "100%", padding: "14px 0" }}
                            />
                        </div>
                        <button style={{ background: TEAL, border: "none", color: "white", padding: "14px 24px", borderRadius: "0 12px 12px 0", fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: "0.875rem", cursor: "pointer" }}>
                            Buscar
                        </button>
                    </div>
                </div>
            </section>

            {/* ═══ CATEGORIES ═══ */}
            <div style={{ background: "white", borderBottom: "1px solid #f0f4f8", padding: "0 24px" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", gap: 4, overflowX: "auto", padding: "12px 0" }}>
                    {CATEGORIES.map(cat => (
                        <button key={cat} onClick={() => setActiveCategory(cat)} style={{ background: activeCategory === cat ? TEAL : "transparent", color: activeCategory === cat ? "white" : "#6b7280", border: `1.5px solid ${activeCategory === cat ? TEAL : "#e5e7eb"}`, borderRadius: 50, padding: "7px 18px", fontFamily: "'Montserrat',sans-serif", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer", whiteSpace: "nowrap" as const, transition: "all 0.18s", flexShrink: 0 }}>
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* ═══ CONTENT ═══ */}
            <section style={{ background: "#f8fbff", padding: "64px 24px 80px" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                    {blogCatalog.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "80px 0" }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 64, color: "#d1d5db", display: "block", marginBottom: 16 }}>article</span>
                            <h3 style={{ fontFamily: "'Montserrat',sans-serif", color: NAVY, fontSize: "1.4rem", fontWeight: 700, margin: "0 0 8px" }}>Próximamente</h3>
                            <p style={{ color: "#6b7280", fontSize: "0.95rem" }}>Estamos preparando artículos de salud para ti. ¡Vuelve pronto!</p>
                        </div>
                    ) : (
                        <>
                            {/* Featured */}
                            {featured && (
                                <div
                                    onClick={() => handleOpen(String(featured.idBlog))}
                                    style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a4a 100%)`, borderRadius: 24, padding: "48px 48px", position: "relative", overflow: "hidden", cursor: "pointer", transition: "transform 0.2s", marginBottom: 40 }}
                                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)"}
                                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.transform = "translateY(0)"}
                                >
                                    <div style={{ position: "absolute", inset: 0, opacity: 0.06, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
                                    <div style={{ position: "absolute", right: -60, bottom: -60, width: 300, height: 300, borderRadius: "50%", background: "rgba(74,188,201,0.15)" }} />
                                    <div style={{ position: "relative", zIndex: 2, maxWidth: 680 }}>
                                        <span style={{ display: "inline-block", background: TEAL, color: "white", fontFamily: "'Montserrat',sans-serif", fontSize: "0.72rem", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, padding: "5px 14px", borderRadius: 50, marginBottom: 20 }}>
                                            Artículo destacado
                                        </span>
                                        <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "clamp(1.5rem,3vw,2.2rem)", fontWeight: 800, color: "white", margin: "0 0 16px", lineHeight: 1.2 }}>
                                            {featured.titulo}
                                        </h2>
                                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <span className="material-symbols-outlined" style={{ fontSize: 18, color: "white" }}>person</span>
                                            </div>
                                            <div>
                                                <div style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600, fontSize: "0.875rem" }}>{featured.autor || "HeyDoctor"}</div>
                                                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
                                                    {format(new Date(featured.fechaPublicacion), "dd 'de' MMMM, yyyy", { locale: es })}
                                                </div>
                                            </div>
                                            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, color: TEAL, fontFamily: "'Montserrat',sans-serif", fontWeight: 600, fontSize: "0.875rem" }}>
                                                Leer artículo
                                                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Grid */}
                            {rest.length > 0 && (
                                <>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                                        <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "1.15rem", fontWeight: 700, color: NAVY, margin: 0 }}>Más artículos</h3>
                                        <span style={{ color: "#6b7280", fontSize: "0.85rem" }}>{filtered.length} artículos</span>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 }}>
                                        {rest.map((blog) => {
                                            const tag = getCategory(blog.titulo);
                                            return (
                                                <div
                                                    key={blog.idBlog}
                                                    onClick={() => handleOpen(String(blog.idBlog))}
                                                    style={{ background: "white", border: "1.5px solid #eef2f7", borderRadius: 16, padding: "28px 24px", cursor: "pointer", transition: "all 0.22s", display: "flex", flexDirection: "column", gap: 12 }}
                                                    onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.transform = "translateY(-4px)"; el.style.boxShadow = "0 12px 32px rgba(74,188,201,0.15)"; el.style.borderColor = TEAL; }}
                                                    onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.transform = "translateY(0)"; el.style.boxShadow = "none"; el.style.borderColor = "#eef2f7"; }}
                                                >
                                                    <span style={{ display: "inline-block", background: TAG_COLORS[tag] || "#f0f9fa", color: TAG_TEXT_COLORS[tag] || TEAL, fontFamily: "'Montserrat',sans-serif", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" as const, padding: "4px 12px", borderRadius: 50, alignSelf: "flex-start" }}>
                                                        {tag}
                                                    </span>
                                                    <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "1rem", fontWeight: 700, color: NAVY, margin: 0, lineHeight: 1.4, flex: 1 }}>{blog.titulo}</h3>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 12, borderTop: "1px solid #f0f4f8" }}>
                                                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(74,188,201,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                            <span className="material-symbols-outlined" style={{ fontSize: 14, color: TEAL }}>person</span>
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{blog.autor || "HeyDoctor"}</div>
                                                            <div style={{ fontSize: "0.73rem", color: "#9ca3af" }}>{format(new Date(blog.fechaPublicacion), "dd MMM yyyy", { locale: es })}</div>
                                                        </div>
                                                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: TEAL }}>open_in_new</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
